import { useQuery } from '@apollo/client/react'

import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

import { EngagementReplySuggestions_SavedReply } from '../../components/EngagementReplySuggestions/EngagementReplySuggestions'
import { SavedReplyItem_SavedReply } from '../../components/SavedRepliesPopover/SavedReplyItem'

export const SavedRepliesQuery = graphql(
  /* GraphQL */ `
    query SavedRepliesQuery($input: SavedRepliesInput!) {
      savedReplies(input: $input) {
        savedReplies {
          ...SavedReplyItem_SavedReply
          ...EngagementReplySuggestions_SavedReply
        }
      }
    }
  `,
  [SavedReplyItem_SavedReply, EngagementReplySuggestions_SavedReply],
)

export type SavedReply = NonNullable<
  NonNullable<
    ResultOf<typeof SavedRepliesQuery>['savedReplies']
  >['savedReplies']
>[number]

type UseSavedRepliesReturn = {
  savedReplies: SavedReply[] | null
  error: string | null
  loading: boolean
}

// Stable empty array reference to prevent unnecessary re-renders
const EMPTY_SAVED_REPLIES: SavedReply[] = []

/**
 * Hook to fetch the saved replies for a given organization.
 * @returns {UseSavedRepliesReturn} An object containing the fetchSavedReplies function
 *
 * @example
 * const { fetchSavedReplies } = useSavedReplies()
 * const { data, loading, error } = await fetchSavedReplies({
 *   organizationId: '123',
 * })
 */
export function useSavedRepliesQuery(): UseSavedRepliesReturn {
  const organizationId = useCurrentOrganization()?.id ?? ''

  const { data, loading, error } = useQuery(SavedRepliesQuery, {
    variables: {
      input: { organizationId },
    },
    fetchPolicy: 'cache-first',
    skip: !organizationId,
  })

  if (loading)
    return { savedReplies: EMPTY_SAVED_REPLIES, error: null, loading: true }

  let errorMessage: string | null = null

  if (error) {
    errorMessage = 'Error fetching saved replies'
  }

  return {
    savedReplies: data?.savedReplies.savedReplies ?? null,
    error: errorMessage,
    loading,
  }
}
